// src/stores/TeamsStore.js - Zustand Store for Microsoft Teams integration
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useTeamsStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Connection status
    teamsConnection: null,
    teamsConnectionLoading: false,

    // OAuth flow state (handles redirect to Microsoft)
    oauthUrl: null,
    oauthInitiating: false,

    // Disconnection
    disconnecting: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      teamsConnection: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS (NO RETURN VALUES)
    // ================================

    /**
     * Load Microsoft Teams connection status - NO RETURN VALUE
     */
    loadTeamsConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamsStore: Socket service not initialized');
        return;
      }

      console.log('🔌 TeamsStore: Loading connection status');
      set({
        teamsConnectionLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_TEAMS_CONNECTION_STATUS, {});
    },

    /**
     * Start OAuth flow - generates OAuth URL
     * Frontend will redirect user to Microsoft
     */
    startTeamsOAuth: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamsStore: Socket service not initialized');
        return;
      }

      console.log('🚀 TeamsStore: Starting OAuth flow');
      set({
        oauthInitiating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.START_TEAMS_OAUTH, {});
    },

    /**
     * Disconnect Microsoft Teams integration
     */
    disconnectTeams: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamsStore: Socket service not initialized');
        return;
      }

      console.log('🔌 TeamsStore: Disconnecting Microsoft Teams');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_TEAMS, {});
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get connection status string
     */
    getConnectionStatus: () => {
      const { teamsConnection } = get();
      return teamsConnection?.connection_status || 'disconnected';
    },

    /**
     * Check if connected
     */
    isTeamsConnected: () => {
      const status = get().getConnectionStatus();
      return status === 'connected';
    },

    /**
     * Check if token is expired
     */
    isTokenExpired: () => {
      const { teamsConnection } = get();
      return teamsConnection?.token_expired === true;
    },

    /**
     * Get user-friendly status message
     */
    getStatusMessage: () => {
      const status = get().getConnectionStatus();
      const messages = {
        disconnected: 'Not connected to Microsoft Teams',
        connected: 'Connected to Microsoft Teams',
        token_expired: 'Connection expired - please reconnect'
      };
      return messages[status] || 'Unknown status';
    },

    /**
     * Get connected account email
     */
    getAccountEmail: () => {
      const { teamsConnection } = get();
      return teamsConnection?.account_email || null;
    },

    /**
     * Check if Teams has required permission
     */
    hasPermission: () => {
      const { teamsConnection } = get();
      return teamsConnection?.has_permission || false;
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setTeamsConnectionLoaded: (data) => {
      console.log('📡 TeamsStore: Connection status received');

      set(state => ({
        teamsConnection: data,
        teamsConnectionLoading: false,
        isConnected: data?.connection_status === 'connected',
        cache: {
          ...state.cache,
          teamsConnection: data,
          lastFetch: new Date()
        }
      }));
    },

    _setOAuthUrlGenerated: (data) => {
      console.log('📡 TeamsStore: OAuth URL generated');

      set({
        oauthUrl: data.url,
        oauthInitiating: false
      });
    },

    _setTeamsDisconnected: (data) => {
      console.log('📡 TeamsStore: Teams disconnected');

      set({
        teamsConnection: null,
        disconnecting: false,
        isConnected: false,
        cache: {
          teamsConnection: null,
          lastFetch: null
        }
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================

    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },

    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },

    _handleAuthReset: () => {
      console.log('🧹 TeamsStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        teamsConnection: null,
        teamsConnectionLoading: false,
        oauthUrl: null,
        oauthInitiating: false,
        disconnecting: false,
        isConnected: false,
        cache: {
          teamsConnection: null,
          lastFetch: null
        },
        _initialized: false
      });
    },

    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 TeamsStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load Teams connection status if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 TeamsStore: Auto-loading Teams connection status (first time)');
        state.loadTeamsConnectionStatus();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for TeamsStore
 * Called by SocketContext on auth success
 */
export const initializeTeamsStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ TeamsStore: Cannot initialize - socketManager not available');
    return;
  }

  console.log('🎧 TeamsStore: Initializing socket listeners');

  const store = useTeamsStore.getState();

  // Mark as initialized
  useTeamsStore.setState({ _initialized: true });

  // Central event handler
  const handleSocketEvent = (eventName, responseData) => {
    const store = useTeamsStore.getState();

    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.TEAMS_CONNECTION_STATUS_LOADED:
        store._setTeamsConnectionLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.TEAMS_OAUTH_URL_GENERATED:
        store._setOAuthUrlGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.TEAMS_DISCONNECTED:
        store._setTeamsDisconnected(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_TEAMS_CONNECTION_STATUS_FAILED:
        useTeamsStore.setState({ teamsConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_TEAMS_OAUTH_FAILED:
        useTeamsStore.setState({ oauthInitiating: false });
        break;

      case SOCKET_EVENTS.DISCONNECT_TEAMS_FAILED:
        useTeamsStore.setState({ disconnecting: false });
        console.log('❌ TeamsStore: Disconnect failed -', responseData.message);
        break;

      default:
        console.warn('⚠️ TeamsStore: Unhandled event:', eventName);
    }
  };

  // Set up individual event listeners
  const teamsEvents = [
    // Success events
    SOCKET_EVENTS.TEAMS_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.TEAMS_OAUTH_URL_GENERATED,
    SOCKET_EVENTS.TEAMS_DISCONNECTED,
    // Error events
    SOCKET_EVENTS.GET_TEAMS_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_TEAMS_OAUTH_FAILED,
    SOCKET_EVENTS.DISCONNECT_TEAMS_FAILED
  ];

  teamsEvents.forEach(eventName => {
    socketManager.universalHandler.on(eventName, (data) => {
      handleSocketEvent(eventName, { event: eventName, data });
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useTeamsStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useTeamsStore.getState()._initialize(socketManager);

  console.log('✅ TeamsStore: Socket listeners initialized');
};

export default useTeamsStore;
